// ---------------------------------------------------------------------------
// Alerts page — active alerts, severity filtering, acknowledgement, history
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertsList, type AlertItem, type AlertSeverity, type AlertStatus } from '../components/AlertsList';
import {
  SystemHealthIndicator,
  type ComponentHealth,
  type HealthStatus,
} from '../components/SystemHealthIndicator';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FilterSeverity = 'all' | AlertSeverity;
type ViewMode = 'active' | 'history';

const SEVERITY_FILTER_KEYS: FilterSeverity[] = ['all', 'critical', 'high', 'medium', 'low'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AlertsPage() {
  const { t } = useTranslation('admin');
  const severityLabels = t('alerts.severityFilter', { returnObjects: true }) as Record<string, string>;
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [systemHealth, setSystemHealth] = useState<{
    overall: HealthStatus;
    components: ComponentHealth[];
  }>({ overall: 'healthy', components: [] });
  const [severityFilter, setSeverityFilter] = useState<FilterSeverity>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';

      const [alertsRes, healthRes] = await Promise.all([
        fetch(`${baseUrl}/v1/admin/system/alerts`).then((r) => r.json()),
        fetch(`${baseUrl}/v1/admin/system/health`).then((r) => r.json()),
      ]);

      const alertsData = (alertsRes as { data: AlertItem[] }).data ?? [];
      setAlerts(alertsData.map((a) => ({ ...a, status: a.status ?? 'active' })));

      const healthData = (healthRes as {
        data: { overall: HealthStatus; components: ComponentHealth[] };
      }).data;
      if (healthData) {
        setSystemHealth(healthData);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('alerts.errors.loadFailed');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter logic
  const filteredAlerts = alerts
    .filter((a) => {
      if (viewMode === 'active') return a.status === 'active' || a.status === 'acknowledged';
      return a.status === 'resolved' || a.status === 'muted';
    })
    .filter((a) => severityFilter === 'all' || a.severity === severityFilter);

  // Actions
  const handleAcknowledge = useCallback(
    async (alertId: string) => {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
        await fetch(`${baseUrl}/v1/admin/system/alerts/${alertId}/acknowledge`, {
          method: 'POST',
        });
        setAlerts((prev) =>
          prev.map((a) =>
            a.id === alertId
              ? { ...a, status: 'acknowledged' as AlertStatus, acknowledgedAt: new Date().toISOString() }
              : a,
          ),
        );
      } catch {
        // Silently fail — alert still shows
      }
    },
    [],
  );

  const handleResolve = useCallback((alertId: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === alertId ? { ...a, status: 'resolved' as AlertStatus } : a)),
    );
  }, []);

  const handleMute = useCallback((alertId: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === alertId ? { ...a, status: 'muted' as AlertStatus } : a)),
    );
  }, []);

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadData} />;

  const activeCount = alerts.filter(
    (a) => a.status === 'active' || a.status === 'acknowledged',
  ).length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="font-display text-3xl text-ink">{t('alerts.headings.title')}</h1>
          {activeCount > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium bg-danger-tint text-danger">
              {t('alerts.badge.activeCount', { count: activeCount })}
            </span>
          )}
        </div>
        <SystemHealthIndicator
          overall={systemHealth.overall}
          components={systemHealth.components}
        />
      </div>

      {/* View mode and severity filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        {/* View mode toggle */}
        <div className="flex items-center gap-1 bg-surface-sunken rounded-sm p-1">
          <button
            type="button"
            onClick={() => setViewMode('active')}
            className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${
              viewMode === 'active'
                ? 'bg-surface-raised text-ink border border-hairline'
                : 'text-ink-muted hover:text-ink'
            }`}
          >
            {t('alerts.viewMode.active')}
          </button>
          <button
            type="button"
            onClick={() => setViewMode('history')}
            className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${
              viewMode === 'history'
                ? 'bg-surface-raised text-ink border border-hairline'
                : 'text-ink-muted hover:text-ink'
            }`}
          >
            {t('alerts.viewMode.history')}
          </button>
        </div>

        {/* Severity filter */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-muted">{severityLabels.label}</span>
          {SEVERITY_FILTER_KEYS.map((sev) => (
            <button
              key={sev}
              type="button"
              onClick={() => setSeverityFilter(sev)}
              className={`px-2.5 py-1 text-xs font-medium rounded-sm transition-colors ${
                severityFilter === sev
                  ? 'bg-ink-surface text-white'
                  : 'bg-surface-sunken text-ink-muted hover:bg-surface-sunken'
              }`}
            >
              {severityLabels[sev]}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts list */}
      <AlertsList
        alerts={filteredAlerts}
        onAcknowledge={handleAcknowledge}
        onResolve={handleResolve}
        onMute={handleMute}
      />
    </div>
  );
}
